import { parseTimeSpent } from './timeSpent';

export enum HourPrecision {
  FifteenMinutes = 'FifteenMinutes',
  Hour = 'Hour',
  ThirtyMinutes = 'ThirtyMinutes',
}

const HourPrecisionIntervals: Record<HourPrecision, number[]> = {
  [HourPrecision.FifteenMinutes]: [0, 15, 30, 45, 60],
  [HourPrecision.Hour]: [0, 60],
  [HourPrecision.ThirtyMinutes]: [0, 30, 60],
};

export type RoundMethod = 'down' | 'nearest' | 'up';

export interface MinutesToDecimalOptions {
  readonly decimalPrecision?: number;
  readonly precisionInterval?: HourPrecision;
  readonly round?: 'down' | 'nearest' | 'up';
}

const selectIntervalBound = (
  minutes: number,
  bounds: [number, number],
  options?: MinutesToDecimalOptions,
) => {
  const round = options?.round ?? 'down';

  if (minutes >= bounds[0] && minutes < bounds[1]) {
    if (minutes === bounds[0]) {
      return bounds[0];
    }
    if (round === 'down') {
      return bounds[0];
    }
    if (round === 'up') {
      return bounds[1];
    }
    const midpoint = bounds[0] + (bounds[1] - bounds[0]) / 2;
    if (minutes < midpoint) {
      return bounds[0];
    }
    return bounds[1];
  }
  return null;
};

const roundToNearestHourInterval = (minutes: number, options?: MinutesToDecimalOptions) => {
  const precision = options?.precisionInterval ?? HourPrecision.FifteenMinutes;

  const intervals = HourPrecisionIntervals[precision];

  for (let i = 1; i < intervals.length; i++) {
    const bound = selectIntervalBound(minutes, [intervals[i - 1], intervals[i]], options);
    if (bound !== null) {
      return bound;
    }
  }
  throw new Error(
    `Could not determine a proper interval precision for the provided '${minutes}' minutes!`,
  );
};

export const minutesToHours = (minutes: number, options?: MinutesToDecimalOptions): number => {
  const mins = Math.max(0, minutes);

  if (mins >= 60) {
    const { hours, minutes: leftover } = parseTimeSpent(minutes);
    if (leftover > 60) {
      throw new Error(
        "The 'parseTimeSpent' method unexpectedly returned a 'minutes' value > 60! This will " +
          'lead to an infinite recursion!',
      );
    }
    return hours + minutesToHours(leftover, options);
  }

  if (options?.precisionInterval) {
    const interval = roundToNearestHourInterval(minutes, {
      precisionInterval: options.precisionInterval,
      round: options.round,
    });
    return minutesToHours(interval, { decimalPrecision: options.decimalPrecision });
  }
  if (options?.decimalPrecision !== undefined) {
    const decimalPrecision = Math.max(0, options.decimalPrecision);
    const factor = 10 ** decimalPrecision;
    return Math.round((minutes / 60) * factor) / factor;
  }
  return minutes / 60;
};
